<template>
  <div class="page">
    <h1 class="page-title">해양 쓰레기 신고서</h1>

    <!-- 발견 위치 -->
    <section class="card">
      <h2 class="card-title">발견 위치 *</h2>

      <!-- 네이버 지도 (정사각형 큰 사이즈) -->
      <div class="map">
        <div id="naverMap" class="map-area-large"></div>
      </div>

      <p class="coords" v-if="isLocationLoading">📍 현재 위치를 가져오고 있습니다...</p>
      <p class="coords" v-else>📍 지도를 드래그하여 정확한 위치를 선택하세요</p>
      <p class="coords">좌표: ({{ coords.lng.toFixed(4) }}, {{ coords.lat.toFixed(4) }})</p>
      <p class="coords" v-if="photoGpsStatus">{{ photoGpsStatus }}</p>
    </section>

    <!-- 사진 첨부 -->
    <section class="card">
      <h2 class="card-title">사진 첨부 (최대 10MB, JPG/PNG)</h2>

      <div class="upload">
        <label class="dropzone" @dragover.prevent @drop.prevent="onDrop">
          <input ref="fileInput" type="file" accept="image/png, image/jpeg" multiple class="file" @change="onFiles"/>

          <div class="drop-content">
            <span class="icon">📷</span>
            <span>클릭하거나 이미지를 끌어다 놓으세요</span>
          </div>
        </label>

        <ul class="thumbs" v-if="previews.length">
          <li v-for="(src, i) in previews" :key="i">
            <img :src="src" alt="" />
          </li>
        </ul>
      </div>
    </section>

    <!-- 상세 추가 설명 -->
    <section class="card">
      <h2 class="card-title">(선택) 추가 설명 (최대 200자)</h2>
      
      <InputField
        v-model="form.description"
        as="textarea"
        placeholder="쓰레기 종류, 수거 시 주의점 등을 자유롭게 작성해주세요"
        :rows="4"
      />
    </section>

    <!-- 보안 인증 섹션 -->
    <section class="card" v-if="showTurnstile">
      <h2 class="card-title">🛡️ 보안 인증</h2>
      <p class="security-notice">
        봇 공격 방지를 위한 보안 인증입니다. 아래 체크박스를 클릭해주세요.
      </p>
      
      <TurnstileWidget
        ref="turnstileWidget"
        :site-key="turnstileSiteKey"
        theme="light"
        size="normal"
        @token="onTurnstileToken"
        @error="onTurnstileError"
      />
      
      <div v-if="turnstileError" class="turnstile-error">
        {{ turnstileError }}
      </div>
    </section>

    <div class="actions">
      <button 
        class="primary" 
        @click="submit" 
        :disabled="isSubmitting"
        :class="{ 'loading': isSubmitting }"
      >
        <span v-if="isSubmitting">제보 중...</span>
        <span v-else>해양쓰레기 제보하기</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import InputField from '@/components/common/InputField.vue'
import TurnstileWidget from '@/components/common/TurnstileWidget.vue'
import http from '@/api/http'
import exifr from 'exifr'

const router = useRouter()

const form = ref({
  description: ''
})

const coords = ref({ lng: 126.9784, lat: 37.5666 }) // 서울 중심으로 초기값
const map = ref(null)
const marker = ref(null)
const isLocationLoading = ref(false)

const fileInput = ref(null)
const selectedFiles = ref([])     // 실제 업로드 파일들 저장
const previews = ref([])
const photoGpsStatus = ref('')    // "사진 위치 적용됨/없음" 메시지

// Turnstile 관련
const turnstileWidget = ref(null)
const turnstileToken = ref('')
const turnstileError = ref('')
const isSubmitting = ref(false)
const showTurnstile = ref(false)
const turnstileSiteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY

// 네이버 지도 API 키
const clientId = process.env.VUE_APP_NAVER_MAP_CLIENT_ID

// 현재 위치 가져오기
const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation이 지원되지 않는 브라우저입니다.'))
      return
    }

    isLocationLoading.value = true
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        isLocationLoading.value = false
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        })
      },
      (error) => {
        isLocationLoading.value = false
        console.warn('위치 정보를 가져올 수 없습니다:', error.message)
        reject(error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5분간 캐시된 위치 정보 사용
      }
    )
  })
}

// 네이버 지도 API 로드
const loadNaverMapAPI = () => {
  return new Promise((resolve, reject) => {
    if (window.naver && window.naver.maps) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}`
    
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })
}

// 지도 초기화
const initializeMap = () => {
  if (!window.naver || !window.naver.maps) {
    console.error('네이버 지도 API가 로드되지 않았습니다.')
    return
  }

  const mapOptions = {
    center: new window.naver.maps.LatLng(coords.value.lat, coords.value.lng),
    zoom: 15,
    mapTypeControl: false,
    scaleControl: true,
    logoControl: true,
    mapDataControl: true
  }

  map.value = new window.naver.maps.Map('naverMap', mapOptions)
  
  // 지도 중앙에 고정된 마커 생성 (드래그 불가)
  marker.value = new window.naver.maps.Marker({
    position: new window.naver.maps.LatLng(coords.value.lat, coords.value.lng),
    map: map.value,
    draggable: false
  })

  // 지도 드래그 이벤트 리스너 - 지도가 움직일 때마다 중앙 좌표 업데이트
  window.naver.maps.Event.addListener(map.value, 'dragend', function() {
    const center = map.value.getCenter()
    coords.value.lat = center.lat()
    coords.value.lng = center.lng()
    
    // 마커 위치도 지도 중앙으로 업데이트
    marker.value.setPosition(center)
  })

  // 지도 중심이 변경될 때도 좌표 업데이트 (확대/축소 등)
  window.naver.maps.Event.addListener(map.value, 'center_changed', function() {
    const center = map.value.getCenter()
    coords.value.lat = center.lat()
    coords.value.lng = center.lng()
    
    // 마커 위치도 지도 중앙으로 업데이트
    marker.value.setPosition(center)
  })
}

function onFiles(e) {
  loadPreviews([...e.target.files])
}

function onDrop(e) {
  const files = [...e.dataTransfer.files].filter(f => /image\/(png|jpe?g)/.test(f.type))
  loadPreviews(files)
}

async function loadPreviews(files) {
  previews.value = []
  photoGpsStatus.value = ''

  // 1) 파일 필터링(이미지 + 10MB 제한 + 최대 10장)
  const filtered = files
    .filter(f => /image\/(png|jpe?g)/.test(f.type))
    .filter(f => f.size <= 10 * 1024 * 1024)
    .slice(0, 10)

  // 2) submit용 파일 저장
  selectedFiles.value = filtered

  // 3) 미리보기 생성
  filtered.forEach(file => {
    previews.value.push(URL.createObjectURL(file))
  })

  // 4) 첫 번째 사진에서 GPS 읽어서 지도 이동
  if (!filtered.length) return
  try {
    const gps = await exifr.gps(filtered[0]) // { latitude, longitude } 형태
    if (gps?.latitude != null && gps?.longitude != null) {
      coords.value.lat = gps.latitude
      coords.value.lng = gps.longitude
      photoGpsStatus.value = '🗺️ 사진 GPS 위치를 지도에 적용했습니다.'

      // 지도가 이미 로드된 상태면 즉시 반영
      if (map.value && window.naver?.maps) {
        const pos = new window.naver.maps.LatLng(coords.value.lat, coords.value.lng)
        map.value.setCenter(pos)
        if (marker.value) marker.value.setPosition(pos)
      }
    } else {
      photoGpsStatus.value = '❗이 사진에는 위치(GPS) 메타데이터가 없습니다. (지도에서 직접 선택해 주세요)❗'
    }
  } catch (e) {
    console.warn('EXIF GPS 파싱 실패:', e)
    photoGpsStatus.value = '⚠️ 사진 메타데이터를 읽지 못했습니다. (지도에서 직접 선택해 주세요)'
  }
}


// Turnstile 이벤트 핸들러
const onTurnstileToken = (token) => {
  turnstileToken.value = token
  turnstileError.value = ''
}

const onTurnstileError = (error) => {
  turnstileError.value = error
  turnstileToken.value = ''
}

function submit() {
  if (isSubmitting.value) return

  // 사진이 없으면 경고
  if (!previews.value.length) {
    alert('사진을 첨부해주세요.')
    return
  }

  // Turnstile 검증 표시
  if (!showTurnstile.value) {
    showTurnstile.value = true
    alert('봇 방지를 위한 보안 인증을 완료해주세요.')
    return
  }

  // Turnstile 토큰 확인
  if (!turnstileToken.value) {
    alert('보안 인증을 완료해주세요.')
    return
  }

  if (turnstileError.value) {
    alert('보안 인증 중 오류가 발생했습니다. 다시 시도해주세요.')
    if (turnstileWidget.value) {
      turnstileWidget.value.reset()
    }
    return
  }

  isSubmitting.value = true

  // API 명세에 맞게 payload 구성
  const payload = {
    x: coords.value.lng,
    y: coords.value.lat,
    content: form.value.description || '',
    turnstileToken: turnstileToken.value
  }

  // FormData로 multipart/form-data 구성
  const formData = new FormData()

  // payload 부분 (application/json)
  formData.append(
    'payload',
    new Blob([JSON.stringify(payload)], { type: 'application/json' })
  )

  // 사진 파일들 추가 (photos 부분)
  selectedFiles.value.forEach(f => formData.append('photos', f))

  if (!selectedFiles.value.length) {
    alert('사진을 첨부해주세요.')
    return
  }
  // 백엔드로 전송
  submitToBackend(formData)
}

const submitToBackend = async (formData) => {
  try {
    const response = await http.post('/notify', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    console.log('쓰레기 신고 성공:', response.data)
    alert('해양 쓰레기 신고가 성공적으로 접수되었습니다!')
    
    // 성공 시 메인페이지로 이동 (히스토리 스택에 쌓이지 않게 replace 사용)
    router.replace({ name: 'Home' })
  } catch (error) {
    console.error('쓰레기 신고 실패:', error)
    
    // 봇 검증 실패인 경우
    if (error.response && error.response.status === 400 && 
        error.response.data && error.response.data.error === 'Bot verification failed') {
      alert('보안 인증에 실패했습니다. 다시 시도해주세요.')
      if (turnstileWidget.value) {
        turnstileWidget.value.reset()
      }
      showTurnstile.value = true
    } else {
      alert('신고 접수에 실패했습니다. 다시 시도해주세요.')
    }
  } finally {
    isSubmitting.value = false
  }
}

// 라이프사이클 훅
onMounted(async () => {
  try {
    // 먼저 현재 위치를 가져오려고 시도
    try {
      const currentLocation = await getCurrentLocation()
      coords.value = currentLocation
      console.log('현재 위치로 설정:', currentLocation)
    } catch (locationError) {
      console.warn('현재 위치를 가져올 수 없어 기본 위치(서울)를 사용합니다:', locationError.message)
      // 기본값 유지
    }
    
    await loadNaverMapAPI()
    initializeMap()
  } catch (error) {
    console.error('네이버 지도 API 로드 실패:', error)
  }
})

onBeforeUnmount(() => {
  if (map.value) {
    map.value.destroy()
  }
})
</script>

<style scoped>
/* Layout */
.page {
  background: #eef1f7;
  min-height: 100vh;
  padding: 32px 20px 80px;
  max-width: 900px;
  margin: 0 auto;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 20px;
}
.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 20px;
  margin-bottom: 18px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
}
.card-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 14px;
}

/* Map - Large Square */
.map { margin-top: 10px; }
.map-area-large {
  width: 100%;
  height: 400px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}
.coords {
  margin: 8px 2px 0;
  font-size: 12px;
  color: #6b7280;
}

/* Upload */
.upload {
  margin-top: 10px;
}
.dropzone {
  border: 1px dashed #c7cdd8;
  border-radius: 9999px;
  padding: 16px;
  background: #f3f6fb;
  cursor: pointer;
  display: block;
}
.dropzone:hover { background: #eaf0fb; }
.file { display: none; }
.drop-content {
  display: flex; align-items: center; gap: 8px; justify-content: center;
  font-size: 14px; color: #6b7280;
}
.icon { font-size: 18px; }

.thumbs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding: 0;
  list-style: none;
}
.thumbs li { width: 110px; height: 70px; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; }
.thumbs img { width: 100%; height: 100%; object-fit: cover; }

/* Actions */
.actions { display: flex; justify-content: center; margin-top: 16px; }
.primary {
  padding: 12px 22px;
  border-radius: 12px;
  background: #2f327d;
  color: #fff;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}
.primary:hover:not(:disabled) { filter: brightness(0.95); }
.primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.primary.loading {
  background: #6b7280;
}

/* Security Section */
.security-notice {
  font-size: 14px;
  color: #6b7280;
  text-align: center;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.turnstile-error {
  color: #dc2626;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
  padding: 8px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}
</style>

<style>
html, body {
  background: #eef1f7 !important;
  min-height: 100vh;
}
</style>
